import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Refresh, TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { ProductionAssumptionsData, ProductionAssumptionsMetric } from '../../types/widget';
import { apiService } from '../../services/api';

// Helper to format numbers. e.g., 4.75 -> "+4.75"
const formatVariance = (variance: number, isCurrency: boolean = false): string => {
  const prefix = variance > 0 ? '+' : '';
  if (isCurrency) {
    return (
      prefix +
      new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(variance)
    );
  }
  return prefix + variance;
};

// Helper to get variance color
const getVarianceColor = (variance: number, reverse: boolean = false): string => {
  if (variance > 0) return reverse ? 'success.main' : 'error.main'; // Over budget/schedule is bad (red)
  if (variance < 0) return reverse ? 'error.main' : 'success.main'; // Under budget/schedule is good (green)
  return 'text.secondary';
};

// Helper to get variance icon
const getVarianceIcon = (variance: number) => {
  if (variance > 0) return <TrendingUp sx={{ color: 'error.main' }} />;
  if (variance < 0) return <TrendingDown sx={{ color: 'success.main' }} />;
  return <TrendingFlat sx={{ color: 'text.secondary' }} />;
};

const ProductionAssumptionsWidget: React.FC<{ widgetId: string; title: string }> = ({
  title,
}) => {
  const [data, setData] = useState<ProductionAssumptionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getProductionAssumptionsData();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const renderMetric = (metric: ProductionAssumptionsMetric) => {
    const isCurrency = metric.name.includes('Budget');
    const color = getVarianceColor(metric.variance);
    const icon = getVarianceIcon(metric.variance);
    const varianceText = formatVariance(metric.variance, isCurrency);

    return (
      <Grid item xs={12} md={4} key={metric.name}>
        <Paper
          sx={{
            p: 2,
            backgroundColor: '#2D3748',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minWidth: 0, // Prevent overflow
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ wordBreak: 'break-word' }}>
            {metric.name}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Original
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 'bold',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {isCurrency
                ? new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0,
                  }).format(metric.original)
                : metric.original}
            </Typography>
          </Box>
          <Box sx={{ my: 1.5, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Current
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 'bold',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {isCurrency
                ? new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0,
                  }).format(metric.current)
                : metric.current}
            </Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                color,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                flex: '1 1 auto',
                minWidth: 0,
                fontSize: varianceText.length > 15 ? '1rem' : undefined, // Smaller font for very long numbers
              }}
            >
              {varianceText}
            </Typography>
            <Box sx={{ flexShrink: 0 }}>
              {icon}
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No assumptions data available.</Alert>;
  }

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        backgroundColor: '#1A202C',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.productionTitle} (Baseline: {data.baselineVersion})
          </Typography>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton onClick={fetchData} size="small">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2} sx={{ height: '100%' }}>
          {renderMetric(data.metrics.schedule)}
          {renderMetric(data.metrics.budget)}
          {renderMetric(data.metrics.creative)}
        </Grid>
      </Box>
    </Paper>
  );
};

export default ProductionAssumptionsWidget;

